// VRG-733 — react-query hooks for GTM → Articles → Cover.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { articleContentApi, coversApi, type TestsRequest } from 'services/coversApi'

const key = (section: string, slug: string) => ['internal', 'gtm', 'covers', section, slug] as const
const RUNS = ['internal', 'gtm', 'covers', 'runs'] as const

export const useCoverState = (section: string, slug: string, enabled = true) =>
  useQuery({ queryKey: key(section, slug), queryFn: () => coversApi.state(section, slug), staleTime: 10 * 1000, retry: false, enabled })

/** Every locked cover, keyed `section/slug` by the caller; shares the covers cache so an approve refreshes it. */
export const useCoverApprovals = () =>
  useQuery({ queryKey: [...RUNS, 'approvals'], queryFn: () => coversApi.approvals(), staleTime: 10 * 1000, retry: false })

export const useCoverRuns = () => useQuery({ queryKey: RUNS, queryFn: () => coversApi.runs(100), staleTime: 10 * 1000, retry: false })

/** Every write refreshes the article's state AND the global run log (the cap moves). */
const useCoverMutation = <A, R>(section: string, slug: string, fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key(section, slug) })
      qc.invalidateQueries({ queryKey: RUNS })
    },
  })
}

export const useGenerateConcepts = (section: string, slug: string) =>
  useCoverMutation(section, slug, (body: { articleText?: string; title?: string }) => coversApi.generateConcepts(section, slug, body))

export const useGenerateTests = (section: string, slug: string) => useCoverMutation(section, slug, (body: TestsRequest) => coversApi.generateTests(section, slug, body))

export const useGenerateSample = (section: string, slug: string) =>
  useCoverMutation(section, slug, (body: { conceptSetId: string }) => coversApi.generateSample(section, slug, body))

export const useApproveCover = (section: string, slug: string) =>
  useCoverMutation(section, slug, (body: { imageId: string; alt?: string; title?: string; upscale?: boolean }) => coversApi.approve(section, slug, body))

export const useRetryCoverPr = (section: string, slug: string) => useCoverMutation(section, slug, (title?: string) => coversApi.retryPr(section, slug, title))

export const useSetCoverCap = (section: string, slug: string) => useCoverMutation(section, slug, (v: number) => coversApi.setCap(v))

/** Insights → Analytics: what the pieces cost, across all of them. */
export const useInsightsSpend = () => useQuery({ queryKey: [...RUNS, 'spend'], queryFn: () => coversApi.spend(), staleTime: 30_000 })

export const useSetInsightsCap = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (value: number) => coversApi.setCap(value),
    onSettled: () => qc.invalidateQueries({ queryKey: ['internal', 'gtm', 'covers'] }),
  })
}

const commentsKey = (section: string, slug: string) => ['internal', 'gtm', 'covers', 'comments', section, slug] as const

export const useArticleComments = (section: string, slug: string) =>
  useQuery({ queryKey: commentsKey(section, slug), queryFn: () => coversApi.comments(section, slug), staleTime: 15_000 })

export const useAddArticleComment = (section: string, slug: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Parameters<typeof coversApi.addComment>[2]) => coversApi.addComment(section, slug, body),
    onSettled: () => qc.invalidateQueries({ queryKey: commentsKey(section, slug) }),
  })
}

export const useResolveArticleComment = (section: string, slug: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => coversApi.resolveComment(section, slug, id),
    onSettled: () => qc.invalidateQueries({ queryKey: commentsKey(section, slug) }),
  })
}

/** An authenticated image as an object URL (revoked on change/unmount). */
export const useCoverImageUrl = (url: string | null) => {
  const [src, setSrc] = useState<string | null>(null)
  const q = useQuery({ queryKey: ['internal', 'gtm', 'covers', 'blob', url], queryFn: () => coversApi.imageBlob(url!), enabled: !!url, staleTime: Infinity, retry: false })
  useEffect(() => {
    if (!q.data) return setSrc(null)
    const u = URL.createObjectURL(q.data)
    setSrc(u)
    return () => URL.revokeObjectURL(u)
  }, [q.data])
  return { src, isLoading: q.isLoading, error: q.error as Error | null }
}

/** VRG-733 — the website's article manifest, read fresh from its base branch (the list's rows). */
export const useWebsiteManifest = () =>
  useQuery({ queryKey: ['internal', 'gtm', 'article-content', 'manifest'], queryFn: () => articleContentApi.manifest(), staleTime: 60 * 1000, retry: false })

/** VRG-733 — one article's manifest entry and content (the Overview tab). */
export const useArticleDetail = (section: string, slug: string) =>
  useQuery({ queryKey: ['internal', 'gtm', 'article-content', section, slug], queryFn: () => articleContentApi.detail(section, slug), staleTime: 60 * 1000, retry: false })
